import { ReactNode, useMemo } from "react";
import styled from "@emotion/styled";
import { Box, BoxProps, Skeleton, useTheme } from "@mui/material";
import { useNavigate } from "react-router";
import { BackButton } from "@rentablo/components";
import {
  Breadcrumbs,
  FAB_PAGE_BOTTOM_CLEARANCE,
  PANEL_PAGE_BOTTOM_CLEARANCE,
} from "@rentablo/components";
import { Header } from "../../headers";

const Container = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== "layout" && prop !== "hasBottomControlsPanel",
})<{
  layout: "default" | "centered";
  hasBottomControlsPanel?: boolean;
}>`
  display: flex;
  flex-direction: column;
  align-items: ${({ layout }) =>
    layout === "centered" ? "center" : "stretch"};
  margin: ${({ theme }) =>
    theme.type === "finanzfluss" ? theme.spacing(6) : theme.spacing(8, 6, 5)};

  ${({ theme }) => theme.breakpoints.down("sm")} {
    margin: ${({ theme }) => theme.spacing(4)};
    padding-bottom: ${({ hasBottomControlsPanel }) =>
      hasBottomControlsPanel
        ? PANEL_PAGE_BOTTOM_CLEARANCE
        : `${FAB_PAGE_BOTTOM_CLEARANCE}px`};
  }
`;

interface Props extends Omit<BoxProps, "title"> {
  breadcrumbs?: BreadcrumbItem[];
  mobileMenu?: ReactNode;
  hasAddButton?: boolean;
  title?: ReactNode;
  subtitle?: string;
  backUrl?: string | (() => void);
  actionButton?: ReactNode;
  addButtonBreakpoint?: "sm" | "md";
  isFetching?: boolean;
  children: ReactNode;
  isMainPage?: boolean;
  id?: string;
  layout?: "default" | "centered";
  showBorder?: boolean;
  hasBottomControlsPanel?: boolean;
}

export const PageLayout = ({
  breadcrumbs,
  mobileMenu,
  isMainPage,
  actionButton,
  addButtonBreakpoint,
  layout = "default",
  showBorder = false,
  hasAddButton = false,
  isFetching,
  title,
  subtitle,
  backUrl,
  children,
  id,
  hasBottomControlsPanel = false,
  ...rest
}: Props) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const newBreadcrumbs = breadcrumbs ? [...breadcrumbs] : undefined;

  const borderStyles = useMemo(
    () => ({
      borderRadius: "16px",
      border: "2px solid",
      borderColor: theme.palette.border.secondary,
      padding: theme.spacing(6, 4),
    }),
    [theme]
  );

  return (
    <>
      {newBreadcrumbs && <Breadcrumbs items={newBreadcrumbs} />}
      <Container
        id={id}
        layout={layout}
        hasBottomControlsPanel={hasBottomControlsPanel}
        {...rest}
        sx={{ ...rest.sx }}
      >
        <Box
          width="100%"
          sx={{
            maxWidth: layout === "centered" ? "736px" : "none",
            ...(showBorder ? borderStyles : {}),
          }}
        >
          {backUrl && (
            <BackButton
              onClick={
                typeof backUrl === "function"
                  ? backUrl
                  : () => navigate(backUrl)
              }
            />
          )}
          {isFetching ? (
            <Skeleton width={300} />
          ) : (
            <>
              {(title || actionButton || mobileMenu) && (
                <Header
                  isMainPage={isMainPage}
                  title={title}
                  subtitle={subtitle}
                  actionButton={actionButton}
                  mobileMenu={mobileMenu}
                  isAddButton={hasAddButton}
                  addButtonBreakpoint={addButtonBreakpoint}
                />
              )}
            </>
          )}
          {children}
        </Box>
      </Container>
    </>
  );
};
